import { z } from 'zod';
export const DeploymentProfileSchema = z.enum([
    'standalone_onprem',
    'ha_cluster_onprem',
    'cloud_managed',
    'cloud_managed_single_tenant',
    'cloud_multi_tenant',
]);
export const InstallationEnvironmentSchema = z.enum([
    'production',
    'staging',
    'pilot',
    'dr',
    'development',
]);
export const ConnectivityStateSchema = z.enum([
    'connected',
    'degraded',
    'stale',
    'offline',
    'provisioning',
]);
export const ComponentHealthStatusSchema = z.enum(['healthy', 'degraded', 'critical', 'unknown']);
export const FleetComponentHealthSchema = z.object({
    component: z.string(),
    status: ComponentHealthStatusSchema,
    message: z.string().nullable().optional(),
    lastCheckAt: z.iso.datetime(),
});
export const BackupVerificationStatusSchema = z.enum([
    'verified',
    'unverified',
    'failed',
    'pending',
]);
export const BackupPostureSchema = z.object({
    id: z.string(),
    installationId: z.string(),
    lastBackupAt: z.iso.datetime(),
    backupSizeBytes: z.number().int().nonnegative(),
    backupTarget: z.string(),
    verificationStatus: BackupVerificationStatusSchema,
    retentionDays: z.number().int().positive(),
    isFresh: z.boolean(),
    freshnessSlaHours: z.number().int().default(24),
    details: z.record(z.string(), z.unknown()).optional(),
});
export const SigningKeyStatusSchema = z.enum(['active', 'retired', 'revoked']);
export const InstallationSigningKeySchema = z.object({
    id: z.string(),
    installationId: z.string(),
    keyId: z.string(),
    algorithm: z.enum(['hmac-sha256', 'ed25519']),
    publicKeyOrSecretMasked: z.string(),
    status: SigningKeyStatusSchema,
    issuedAt: z.iso.datetime(),
    expiresAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable().optional(),
    revocationReason: z.string().nullable().optional(),
});
export const FleetInstallationSchema = z.object({
    id: z.string(),
    installationIdentifier: z.string(),
    organisationId: z.string(),
    organisationName: z.string().optional(),
    displayName: z.string(),
    environment: InstallationEnvironmentSchema,
    deploymentProfile: DeploymentProfileSchema,
    installedVersion: z.string(),
    targetVersion: z.string().nullable().optional(),
    rolloutRing: z.enum(['internal', 'pilot', 'general', 'canary', 'stable']).default('general'),
    connectivityState: ConnectivityStateSchema,
    lastHeartbeatAt: z.iso.datetime().nullable().optional(),
    heartbeatAgeSeconds: z.number().int().nullable().optional(),
    ipAddress: z.string().nullable().optional(),
    region: z.string().default('in-south-1'),
    hardwareSummary: z.record(z.string(), z.unknown()).optional(),
    componentHealth: z.array(FleetComponentHealthSchema).default([]),
    backupPosture: BackupPostureSchema.nullable().optional(),
    integrationReadiness: z
        .object({
        abdmBridge: z.boolean().default(false),
        pacsDicom: z.boolean().default(false),
        limsInterface: z.boolean().default(false),
        smsGateway: z.boolean().default(false),
    })
        .default({ abdmBridge: false, pacsDicom: false, limsInterface: false, smsGateway: false }),
    activeKeyId: z.string().nullable().optional(),
    isKeyCompromised: z.boolean().default(false),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    version: z.number().int().default(1),
});
export const RegisterInstallationRequestSchema = z.object({
    organisationId: z.string(),
    installationIdentifier: z.string().min(3).max(64),
    displayName: z.string().min(2).max(120),
    environment: InstallationEnvironmentSchema,
    deploymentProfile: DeploymentProfileSchema,
    installedVersion: z.string().min(1),
    rolloutRing: z.enum(['internal', 'pilot', 'general', 'canary', 'stable']).default('general'),
    ipAddress: z.string().optional(),
    region: z.string().default('in-south-1'),
    signingSecret: z.string().min(16),
    hardwareSummary: z.record(z.string(), z.unknown()).optional(),
    integrationReadiness: z
        .object({
        abdmBridge: z.boolean().default(false),
        pacsDicom: z.boolean().default(false),
        limsInterface: z.boolean().default(false),
        smsGateway: z.boolean().default(false),
    })
        .optional(),
});
export const RotateKeyRequestSchema = z.object({
    algorithm: z.enum(['hmac-sha256', 'ed25519']).default('hmac-sha256'),
    newSecretOrKey: z.string().min(16),
    reason: z.string().min(5).max(255),
});
export const RevokeCompromisedKeyRequestSchema = z.object({
    keyId: z.string(),
    reason: z.string().min(5).max(255),
});
//# sourceMappingURL=fleet.js.map